import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { Icon } from "@/components/icons/Icon";
import { Sidebar } from "@/components/sidebar";
import { setAlign } from "@/components/tiptap/utils/editorActions";

import { CollapsedPopover } from "../CollapsedPopover";

import type { DropdownItem } from "@/components/sidebar/Dropdown";
import type { DocumentModel } from "@/models/DocumentModel";

export const ALIGNMENT_OPTIONS = ["left", "center", "right"] as const;

export type Alignment = (typeof ALIGNMENT_OPTIONS)[number];

type Props = {
  doc: DocumentModel;
  isCollapsed?: boolean;
};

export const AlignmentButtons = observer(function AlignmentButtons({ doc, isCollapsed }: Props) {
  const { tiptap, editorState } = doc.editor;
  const t = useTranslations("sidebar");

  if (!tiptap) {
    return null;
  }

  const baseItems = [
    {
      title: t("textStyle.alignLeft"),
      icon: <Icon name="AlignTextLeft" />,
      active: editorState.blockAlign === "left",
      hintKey: "textAlignLeft",
      hotkey: "align-left",
      onClick: () => {
        setAlign(doc, "left");
      },
    },
    {
      title: t("textStyle.alignCenter"),
      icon: <Icon name="AlignTextCenter" />,
      active: editorState.blockAlign === "center",
      hintKey: "textAlignCenter",
      hotkey: "align-center",
      onClick: () => {
        setAlign(doc, "center");
      },
    },
    {
      title: t("textStyle.alignRight"),
      icon: <Icon name="AlignTextRight" />,
      active: editorState.blockAlign === "right",
      hintKey: "textAlignRight",
      hotkey: "align-right",
      onClick: () => {
        setAlign(doc, "right");
      },
    },
  ] as const satisfies DropdownItem[];

  const renderedButtons = baseItems.map((item) => {
    return (
      <Sidebar.Button
        key={item.title}
        icon={item.icon}
        aria-label={item.title}
        title={item.title}
        hotkey={item.hotkey}
        className="w-9.5"
        isActive={item.active}
        role="radio"
        aria-checked={item.active}
        onClick={item.onClick}
        hintKey={item.hintKey}
        inverted
      />
    );
  });

  const activeIcon = baseItems.find((item) => item.active)?.icon;
  const { hasMathIndentation } = doc.editor.editorState;

  if (isCollapsed) {
    const mathItems = editorState.isParagraphNode
      ? ([
          {
            type: "separator",
          },
          {
            title: t("textStyle.alignMath"),
            icon: <Icon name="AlignMath" />,
            active: !!hasMathIndentation,
            toggle: true,
            onClick: (e?: React.MouseEvent) => {
              e?.preventDefault();
              setAlign(doc, "math");
            },
          },
        ] satisfies DropdownItem[])
      : null;

    const items = mathItems ? [...baseItems, ...mathItems] : baseItems;

    return (
      <CollapsedPopover
        icon={activeIcon || <Icon name="AlignTextLeft" />}
        tooltipTitle={t("textStyle.align")}
        items={items}
        itemsRole="radio"
      />
    );
  }

  return (
    <div role="radiogroup" aria-label={t("toolbars.alignment")} className="grid grid-cols-3 gap-1">
      {renderedButtons}
    </div>
  );
});
